// src/contexts/AuthContext.js
import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import api from '../utils/api';

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [modules, setModules] = useState([]);
  const [theme, setTheme] = useState(null);
  const [loading, setLoading] = useState(true);

  const applyTheme = useCallback((t) => {
    if (!t?.css_variables) return;
    try {
      const vars = typeof t.css_variables === 'string' ? JSON.parse(t.css_variables) : t.css_variables;
      const root = document.documentElement;
      Object.entries(vars).forEach(([k, v]) => root.style.setProperty(k, v));
      setTheme(t);
    } catch {}
  }, []);

  const fetchMe = useCallback(async () => {
    try {
      const { data } = await api.get('/auth/me');
      setUser(data.user);
      setModules(data.modules || []);
      if (data.theme) applyTheme(data.theme);
    } catch {
      localStorage.removeItem('crm_token');
    } finally {
      setLoading(false);
    }
  }, [applyTheme]);

  useEffect(() => {
    if (localStorage.getItem('crm_token')) fetchMe();
    else setLoading(false);
  }, [fetchMe]);

  const login = async (username, password) => {
    const { data } = await api.post('/auth/login', { username, password });
    localStorage.setItem('crm_token', data.token);
    setUser(data.user);
    await fetchMe();
    return data;
  };

  const logout = () => {
    localStorage.removeItem('crm_token');
    setUser(null);
    setModules([]);
  };

  const hasModule = (name) => modules.some(m => m.name === name && m.is_active);
  const hasRole = (...roles) => roles.includes(user?.role);

  return (
    <AuthContext.Provider value={{ user, modules, theme, loading, login, logout, hasModule, hasRole, applyTheme, fetchMe }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
